import { useState, useEffect, useRef, useCallback } from 'react';
import { 
    FaSearch, 
    FaTimes, 
    FaNewspaper, 
    FaMapMarkerAlt, 
    FaUserTie, 
    FaCalendarAlt,
    FaDumbbell 
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAccessibility } from '../../context/AccessibilityContext/AccessibilityContext';
import { debounce } from 'lodash';
import { searchContent } from '../../services/api';
import NewsModal from '../NewsModal/NewsModal';
import EventModal from '../EventModal/EventModal';
import style from './searchbar.module.scss';

const SearchBar = ({ 
    placeholder = "Поиск новостей, тренеров, мероприятий...", 
    isMobile = false,
    searchType = 'all', // 'all', 'news', 'coaches', 'events', 'programs', 'branches'
    onSearch = null,
    customResults = []
}) => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [query, setQuery] = useState('');
    const [groupedResults, setGroupedResults] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const searchRef = useRef(null);
    const { settings } = useAccessibility();

    const performSearch = useCallback(debounce(async (searchQuery) => {
        if (searchQuery.length < 2) {
            setGroupedResults({});
            return;
        }

        setIsLoading(true);
        
        try {
            if (searchType === 'all' || searchType === 'news') {
                const response = await searchContent(searchQuery);
                
                const grouped = {};
                
                // Группируем результаты по типам
                if (response.news && response.news.length > 0) {
                    grouped['news'] = response.news;
                }
                if (response.coaches && response.coaches.length > 0) {
                    grouped['coaches'] = response.coaches;
                }
                if (response.events && response.events.length > 0) {
                    grouped['events'] = response.events;
                }
                if (response.programs && response.programs.length > 0) {
                    grouped['programs'] = response.programs;
                }
                if (response.branches && response.branches.length > 0) {
                    grouped['branches'] = response.branches;
                }
                
                setGroupedResults(grouped);
            } else if (searchType === 'facilities' && onSearch) {
                onSearch(searchQuery);
            }
        } catch (error) {
            console.error('Search error:', error);
            setGroupedResults({});
        } finally {
            setIsLoading(false);
        }
    }, 300), [searchType, onSearch]);

    useEffect(() => {
        if (searchType === 'facilities' && customResults.length > 0) {
            setGroupedResults({
                facilities: customResults
            });
        }
    }, [customResults, searchType]);

    useEffect(() => {
        performSearch(query);
        return () => performSearch.cancel();
    }, [query, performSearch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (item) => {    
        console.log('Selected item:', item); // Debug log
        
        // FIX: Check both 'type' and 'ype' properties for compatibility
        const itemType = item.type || item.ype;
        
        if (itemType === 'news') {
            setSelectedItem(item);
            setIsFocused(false);
            console.log('Opening news modal:', item); // Debug log
        } else if (itemType === 'event') {
            setSelectedItem(item);
            setIsFocused(false);
        }
        setQuery('');
        setGroupedResults({});
    };

    const handleCloseModal = () => {
        setSelectedItem(null);
    };

    const clearSearch = () => {
        setQuery('');
        setGroupedResults({});
    };

    const hasResults = Object.values(groupedResults).some(items => items.length > 0);

    const getResultIcon = (type) => {
        switch (type) {
            case 'event':
                return <FaCalendarAlt className={style.resultIcon} />;
            case 'news':
            default:
                return <FaNewspaper className={style.resultIcon} />;
        }
    };

    const getGroupTitle = (type) => {
        switch (type) {
            case 'events':
                return 'Мероприятия';
            case 'news':
                return 'Новости';
            case 'coaches':
                return 'Тренеры';
            case 'programs':
                return 'Программы';
            case 'branches':
                return 'Филиалы';
            case 'facilities':
                return 'Объекты';
            default:
                return 'Результаты';
        }
    };

    // FIX: Get item type for individual result items
    const getItemType = (item) => {
        return item.type || item.ype || 'news'; // Fallback to 'news' if neither exists
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div 
            className={`${style.searchContainer} ${isMobile ? style.mobileSearch : ''}`} 
            ref={searchRef}
            data-contrast={settings.contrast}
        >
            <div className={style.searchInputContainer}>
                <FaSearch className={style.searchIcon} />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    placeholder={placeholder}
                    className={style.searchInput}
                    aria-label="Поиск"
                />
                {query && (
                    <button 
                        className={style.clearButton}
                        onClick={clearSearch}
                        aria-label="Очистить поиск"
                    >
                        <FaTimes />
                    </button>
                )}
            </div>

            {isFocused && (isLoading || hasResults) && (
                <div className={style.resultsDropdown}>
                    {isLoading ? (
                        <div className={style.loadingIndicator}>
                            <div className={style.spinner}></div>
                            Поиск...
                        </div>
                    ) : (
                        Object.entries(groupedResults).map(([type, items]) => (
                            items.length > 0 && (
                                <div key={type} className={style.resultGroup}>
                                    <div className={style.resultGroupTitle}>
                                        {getGroupTitle(type)} ({items.length})
                                    </div>
                                    {items.slice(0, 5).map((item) => (
                                        <div
                                            key={`${type}-${item.id}`}
                                            className={style.resultItem}
                                            onClick={() => handleSelect(item)}
                                        >
                                            {item.image ? (
                                                <img 
                                                    src={`${item.Image}`}
                                                    alt="" 
                                                    className={style.resultImage}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div className={style.resultIconContainer}>
                                                {getResultIcon(getItemType(item))}
                                            </div>
                                            <div className={style.resultContent}>
                                                <div className={style.resultTitle}>{item.title}</div>
                                                <div className={style.resultType}>
                                                    {getGroupTitle(type)}
                                                </div>
                                                {item.Text && (
                                                    <div className={style.resultText}>
                                                        {truncateText(item.Text)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )
                        ))
                    )}
                </div>
            )}
            
            {/* FIX: Check both 'type' and 'ype' properties */}
            {selectedItem && (selectedItem.type === 'news' || selectedItem.ype === 'news') && (
                <NewsModal
                    news={selectedItem}
                    onClose={handleCloseModal}
                />
            )}
            {selectedItem && (selectedItem.type === 'event' || selectedItem.ype === 'event') && (
                <EventModal
                    event={selectedItem}
                    onClose={() => setSelectedItem(null)}
                />
            )}
        </div>
    );
};

export default SearchBar;